import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ChevronLeft, ChevronRight, Users, LogIn, LogOut, AlertCircle, Utensils, Car, Sparkles, Mountain, Star, MapPin, Clock, User, Calendar, ExternalLink } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import GuestProfile from '../components/GuestProfile';
import { GuestLink, ReservationLink } from '../components/crm/EntityLinks';
import useConfig from '../hooks/useConfig';
import { formatCurrency } from '../utils/formatters';

// Semaines commencent le lundi
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Static leave colors and labels
const LEAVE_CONFIG = {
  'CONGE': { color: 'bg-green-500', label: 'Congé' },
  'REPOS': { color: 'bg-indigo-500', label: 'Repos' },
  'RECUP': { color: 'bg-amber-500', label: 'Récup' },
  'MALADIE': { color: 'bg-red-500', label: 'Maladie' },
  'FORMATION': { color: 'bg-blue-500', label: 'Formation' },
  'AUTRE': { color: 'bg-gray-500', label: 'Absence' }
};

const getLeaveColor = (type) => {
  return LEAVE_CONFIG[type]?.color || 'bg-gray-400';
};

const getLeaveLabel = (type) => {
  return LEAVE_CONFIG[type]?.label || type;
};

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [serviceEvents, setServiceEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showStaffLeaves, setShowStaffLeaves] = useState(true);
  const [showReservationEvents, setShowReservationEvents] = useState(true);
  const [showServices, setShowServices] = useState(true);
  const [viewingGuest, setViewingGuest] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  // Load channel colors and reservation statuses from config
  const { options: configOptions, loading: configLoading } = useConfig(['CHANNEL', 'LEAVE_TYPE', 'RESERVATION_STATUS', 'EVENT_TYPE']);
  
  // Build channel color map dynamically from config
  const channelColorMap = useMemo(() => {
    const map = {};
    if (!configLoading && configOptions.CHANNEL) {
      configOptions.CHANNEL.forEach(c => {
        map[c.code] = c.color || c.metadata?.color || '#6B7280';
      });
    }
    return map;
  }, [configLoading, configOptions.CHANNEL]);
  
  // Build status visibility map based on ConfigOptions
  const statusVisibilityMap = useMemo(() => {
    const map = {
      // Fallback defaults if config not loaded or metadata missing
      'PENDING': true,
      'CONFIRMED': true,
      'CHECKED_IN': true,
      'CHECKED_OUT': false,
      'CANCELLED': false,
      'NO_SHOW': false,
      'BLOCKED': true,
      'MAINTENANCE': true,
      'FINISHED': false
    };
    if (!configLoading && configOptions.RESERVATION_STATUS) {
      configOptions.RESERVATION_STATUS.forEach(s => {
        const metadata = s.metadata || {};
        // Use code or value (API returns code, but some places use value)
        const statusKey = s.code || s.value;
        // Only override if show_in_calendar is explicitly defined in metadata
        // Otherwise keep the default from the map above
        if (metadata.show_in_calendar !== undefined) {
          map[statusKey] = metadata.show_in_calendar === true;
        }
        // If metadata.show_in_calendar is undefined, keep default from map
      });
      // Handle alias - FINISHED is same as CHECKED_OUT
      map['FINISHED'] = map['CHECKED_OUT'] || false;
    }
    return map;
  }, [configLoading, configOptions.RESERVATION_STATUS]);
  
  // Build source color getter with dynamic colors
  const getSourceColor = useCallback((source, isBlocked) => {
    if (isBlocked) return 'bg-gray-400 text-white';
    return 'text-white';
  }, []);
  
  // Get raw color for inline styles
  const getSourceRawColor = useCallback((source, isBlocked) => {
    if (isBlocked) return '#9CA3AF';
    return channelColorMap[source] || '#6B7280';
  }, [channelColorMap]);
  
  // Check if user can see financial info
  const canSeeFinancials = user?.permissions?.includes('finance.view') || 
                           user?.role?.toLowerCase() === 'propriétaire' ||
                           user?.role?.toLowerCase() === 'owner';

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      // Calculer les dates de début et fin du mois
      const from = format(startOfMonth(currentDate), 'yyyy-MM-dd');
      const to = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      
      const [resaRes, leavesRes, eventsRes] = await Promise.all([
        api.get('/reservations/calendar', { params: { year, month } }),
        api.get('/employees/calendar/leaves', { params: { year, month } }),
        api.get('/reservations/events/all', { params: { from, to } })
      ]);
      
      setReservations(resaRes.data);
      setStaffLeaves(leavesRes.data);
      setServiceEvents(eventsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtenir les services pour un jour donné
  const getServicesForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return serviceEvents.filter(e => e.event_date === dayStr);
  };

  // Build event type config map
  const eventTypeMap = useMemo(() => {
    const defaultMap = {
      'MEAL': { icon: Utensils, color: '#F59E0B', label: 'Repas' },
      'TAXI': { icon: Car, color: '#3B82F6', label: 'Transport' },
      'ACTIVITY': { icon: Star, color: '#22C55E', label: 'Activité' },
      'SPA': { icon: Sparkles, color: '#EC4899', label: 'Spa' },
      'EXCURSION': { icon: Mountain, color: '#8B5CF6', label: 'Excursion' },
      'OTHER': { icon: MapPin, color: '#6B7280', label: 'Autre' }
    };
    
    if (!configLoading && configOptions.EVENT_TYPE) {
      configOptions.EVENT_TYPE.forEach(t => {
        // Map icon name to Lucide component
        const iconMap = {
          'utensils': Utensils,
          'car': Car,
          'star': Star,
          'sparkles': Sparkles,
          'mountain': Mountain,
          'compass': Mountain,
          'map': MapPin,
          'calendar': MapPin
        };
        defaultMap[t.value] = {
          icon: iconMap[t.icon] || MapPin,
          color: t.color || '#6B7280',
          label: t.label
        };
      });
    }
    return defaultMap;
  }, [configLoading, configOptions.EVENT_TYPE]);

  // Get service icon component
  const getServiceIconComponent = (type) => {
    const config = eventTypeMap[type] || eventTypeMap['OTHER'];
    return config.icon;
  };
  
  // Get service color
  const getServiceColor = (type) => {
    const config = eventTypeMap[type] || eventTypeMap['OTHER'];
    return config.color;
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  
  // Utiliser startOfWeek avec weekStartsOn: 1 pour que la semaine commence le Lundi
  // Cela garantit que le tableau WEEKDAYS ['Lun', 'Mar', ...] est aligné correctement
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  
  const days = eachDayOfInterval({
    start: calendarStart,
    end: new Date(calendarStart.getTime() + 41 * 24 * 60 * 60 * 1000)
  });

  const getReservationsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return reservations.filter(r => {
      // Filtrer selon show_in_calendar du statut
      const isVisible = statusVisibilityMap[r.status] !== false;
      if (!isVisible) return false;
      
      // Comparer les dates comme strings pour éviter les problèmes de timezone
      // check_in inclus, check_out exclus (le jour du checkout n'est pas une nuit occupée)
      return dayStr >= r.check_in && dayStr < r.check_out;
    });
  };

  const getLeavesForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return staffLeaves.filter(l => {
      return dayStr >= l.start && dayStr <= l.end;
    });
  };

  const isCheckIn = (day, reservation) => {
    return format(day, 'yyyy-MM-dd') === reservation.check_in;
  };

  const isCheckOut = (day, reservation) => {
    return format(day, 'yyyy-MM-dd') === reservation.check_out;
  };

  // Événements de réservation pour un jour donné
  const getReservationEventsForDay = (day) => {
    const events = [];
    const dayStr = format(day, 'yyyy-MM-dd');
    
    reservations.forEach(r => {
      if (r.is_blocked) return;
      
      // Arrivées
      if (r.check_in === dayStr) {
        events.push({
          type: 'CHECKIN',
          reservation: r,
          label: `→ ${r.guest?.first_name || 'Client'}`,
          color: 'bg-green-500'
        });
      }
      
      // Départs
      if (r.check_out === dayStr) {
        events.push({
          type: 'CHECKOUT',
          reservation: r,
          label: `← ${r.guest?.first_name || 'Client'}`,
          color: 'bg-orange-500'
        });
      }
      
      // Rappel acompte (J-7 avant check-in pour réservations non-OTA sans acompte)
      const channelConfig = configOptions.CHANNEL?.find(c => c.code === r.source);
      const isOta = channelConfig?.metadata?.is_ota === true;
      if (!isOta && !r.deposit_amount) {
        const checkInDate = new Date(r.check_in);
        const reminderDate = new Date(checkInDate);
        reminderDate.setDate(reminderDate.getDate() - 7);
        if (isSameDay(day, reminderDate)) {
          events.push({
            type: 'DEPOSIT_REMINDER',
            reservation: r,
            label: `💰 Acompte: ${r.guest?.first_name || 'Client'}`,
            color: 'bg-amber-500'
          });
        }
      }
    });
    
    return events;
  };

  // If viewing guest profile
  if (viewingGuest) {
    return (
      <GuestProfile 
        guest={viewingGuest}
        onBack={() => setViewingGuest(null)}
        onGuestUpdate={(updated) => setViewingGuest(updated)}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Calendrier</h1>
          <p className="text-muted-foreground mt-1">Réservations et planning du personnel</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Show services toggle */}
          <div className="flex items-center gap-2">
            <Switch 
              id="show-services" 
              checked={showServices} 
              onCheckedChange={setShowServices}
            />
            <Label htmlFor="show-services" className="text-sm cursor-pointer">
              <Utensils className="w-4 h-4 inline mr-1" />
              Services
            </Label>
          </div>
          
          {/* Show reservation events toggle */}
          <div className="flex items-center gap-2">
            <Switch 
              id="show-events" 
              checked={showReservationEvents} 
              onCheckedChange={setShowReservationEvents}
            />
            <Label htmlFor="show-events" className="text-sm cursor-pointer">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Événements
            </Label>
          </div>
          
          {/* Show staff toggle */}
          <div className="flex items-center gap-2">
            <Switch 
              id="show-staff" 
              checked={showStaffLeaves} 
              onCheckedChange={setShowStaffLeaves}
            />
            <Label htmlFor="show-staff" className="text-sm cursor-pointer">
              <Users className="w-4 h-4 inline mr-1" />
              Personnel
            </Label>
          </div>
          
          {/* Month navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-lg font-medium min-w-[180px] text-center">
              {format(currentDate, 'MMMM yyyy', { locale: fr })}
            </span>
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Legend - Dynamic from config - Simplified */}
      <div className="flex flex-wrap gap-4 items-center">
        <span className="text-sm font-medium text-muted-foreground">Réservations:</span>
        {!configLoading && configOptions.CHANNEL && configOptions.CHANNEL.map(channel => (
          <div key={channel.code} className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: channel.color || channel.metadata?.color || '#6B7280' }}
            />
            <span className="text-sm">{channel.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-400"></div>
          <span className="text-sm">🔒 Bloqué</span>
        </div>
        
        {showReservationEvents && (
          <>
            <span className="text-sm font-medium text-muted-foreground ml-4">Événements:</span>
            <div className="flex items-center gap-2">
              <LogIn className="w-4 h-4 text-green-600" />
              <span className="text-sm">Arrivée</span>
            </div>
            <div className="flex items-center gap-2">
              <LogOut className="w-4 h-4 text-orange-600" />
              <span className="text-sm">Départ</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm">Rappel</span>
            </div>
          </>
        )}
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b">
            {WEEKDAYS.map((day) => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-muted-foreground bg-muted/50">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayReservations = getReservationsForDay(day);
              const dayLeaves = showStaffLeaves ? getLeavesForDay(day) : [];
              const dayEvents = showReservationEvents ? getReservationEventsForDay(day) : [];
              const dayServices = showServices ? getServicesForDay(day) : [];
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());
              
              // Compter les arrivées et départs
              const checkIns = dayEvents.filter(e => e.type === 'CHECKIN').length;
              const checkOuts = dayEvents.filter(e => e.type === 'CHECKOUT').length;
              const reminders = dayEvents.filter(e => e.type === 'DEPOSIT_REMINDER').length;

              return (
                <div
                  key={index}
                  className={`calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'ring-2 ring-primary ring-inset' : ''}`}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <div className="text-sm font-medium mb-2 flex items-center justify-between">
                    <span className={isToday ? 'text-primary font-bold' : ''}>
                      {format(day, 'd')}
                    </span>
                    <div className="flex items-center gap-1">
                      {/* Services indicator */}
                      {showServices && dayServices.length > 0 && (
                        <span className="flex items-center text-[10px] text-purple-600" title={`${dayServices.length} service(s)`}>
                          <Utensils className="w-3 h-3" />
                          {dayServices.length > 1 && dayServices.length}
                        </span>
                      )}
                      {/* Event indicators */}
                      {showReservationEvents && checkIns > 0 && (
                        <span className="flex items-center text-[10px] text-green-600" title={`${checkIns} arrivée(s)`}>
                          <LogIn className="w-3 h-3" />
                          {checkIns > 1 && checkIns}
                        </span>
                      )}
                      {showReservationEvents && checkOuts > 0 && (
                        <span className="flex items-center text-[10px] text-orange-600" title={`${checkOuts} départ(s)`}>
                          <LogOut className="w-3 h-3" />
                          {checkOuts > 1 && checkOuts}
                        </span>
                      )}
                      {showReservationEvents && reminders > 0 && (
                        <span className="flex items-center text-[10px] text-amber-600" title="Rappel acompte">
                          <AlertCircle className="w-3 h-3" />
                        </span>
                      )}
                      {dayLeaves.length > 0 && (
                        <Badge variant="outline" className="text-xs px-1 py-0 h-5">
                          <Users className="w-3 h-3 mr-0.5" />
                          {dayLeaves.length}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Services - Show type directly */}
                  {dayServices.length > 0 && (
                    <div className="space-y-0.5 mb-1">
                      {dayServices.slice(0, 2).map((service) => {
                        const ServiceIcon = getServiceIconComponent(service.type);
                        const serviceColor = getServiceColor(service.type);
                        const typeLabel = eventTypeMap[service.type]?.label || service.type;
                        return (
                          <div
                            key={service.id}
                            className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer flex items-center gap-1 transition-colors"
                            style={{ 
                              backgroundColor: `${serviceColor}20`,
                              color: serviceColor
                            }}
                            title={`${typeLabel}: ${service.title} - ${service.reservation?.guest?.first_name || 'Client'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedService(service);
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${serviceColor}40`}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = `${serviceColor}20`}
                          >
                            <ServiceIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">{typeLabel}</span>
                          </div>
                        );
                      })}
                      {dayServices.length > 2 && (
                        <div 
                          className="text-[9px] text-muted-foreground px-1 cursor-pointer hover:text-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            const firstService = dayServices[0];
                            if (firstService) setSelectedService(firstService);
                          }}
                        >
                          +{dayServices.length - 2} autres
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Reservations - avec couleur canal dynamique */}
                  <div className="space-y-1">
                    {dayReservations.map((reservation) => {
                      // Get channel color from config
                      const channelConfig = configOptions.CHANNEL?.find(ch => ch.code === reservation.source);
                      const channelColor = channelConfig?.color || channelConfig?.metadata?.color || '#6B7280';
                      const isBlocked = reservation.is_blocked;
                      
                      return (
                        <div
                          key={reservation.id}
                          className="reservation-block cursor-pointer rounded px-1.5 py-1 text-white text-[11px] font-medium"
                          style={{ 
                            backgroundColor: isBlocked ? '#9CA3AF' : channelColor,
                            borderLeft: `3px solid ${isBlocked ? '#6B7280' : channelColor}`
                          }}
                          onClick={() => navigate(`/reservations?id=${reservation.id}`)}
                          data-testid={`reservation-${reservation.id}`}
                        >
                          <div className="truncate flex items-center gap-1">
                            {isCheckIn(day, reservation) && <LogIn className="w-3 h-3 flex-shrink-0" />}
                            {isCheckOut(day, reservation) && !isCheckIn(day, reservation) && <LogOut className="w-3 h-3 flex-shrink-0" />}
                            {isBlocked ? (
                              <span>🔒 {reservation.blocked_reason || 'Bloqué'}</span>
                            ) : (
                              <span>{reservation.guest?.first_name} {reservation.guest?.last_name?.[0]}.</span>
                            )}
                          </div>
                          {isCheckIn(day, reservation) && !isBlocked && (
                            <div className="text-[10px] opacity-90">
                              {reservation.total_pax} pax · {reservation.nights}n
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Staff leaves - avec type explicite */}
                  {dayLeaves.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {dayLeaves.slice(0, 2).map((leave) => (
                        <div
                          key={leave.id}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${getLeaveColor(leave.type)} text-white truncate flex items-center gap-1`}
                          title={`${leave.employee_name} - ${leave.title || getLeaveLabel(leave.type)}`}
                        >
                          <Users className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">
                            {leave.employee_name.split(' ')[0]} ({getLeaveLabel(leave.type)})
                          </span>
                        </div>
                      ))}
                      {dayLeaves.length > 2 && (
                        <div className="text-[10px] text-muted-foreground text-center">
                          +{dayLeaves.length - 2} autres
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reservation Detail Dialog */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedReservation?.is_blocked ? '🔒 Dates bloquées' : 'Détails de la réservation'}
            </DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              {selectedReservation.is_blocked ? (
                // Blocked dates view
                <>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-sm text-muted-foreground">Raison du blocage</p>
                    <p className="font-medium">{selectedReservation.blocked_reason || 'Usage personnel'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Du</p>
                      <p className="font-medium">
                        {format(new Date(selectedReservation.check_in), 'dd/MM/yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Au</p>
                      <p className="font-medium">
                        {format(new Date(selectedReservation.check_out), 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground italic">
                    Ces dates ne sont pas comptabilisées dans les statistiques.
                  </p>
                </>
              ) : (
                // Regular reservation view
                <>
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${getSourceColor(selectedReservation.source, false)}`}></div>
                    <Badge className={`badge-${selectedReservation.source.toLowerCase()}`}>
                      {selectedReservation.source}
                    </Badge>
                    {selectedReservation.platform_ref && (
                      <span className="text-sm text-muted-foreground">
                        #{selectedReservation.platform_ref}
                      </span>
                    )}
                  </div>

                  <div>
                    <GuestLink 
                      guest={selectedReservation.guest} 
                      className="font-semibold text-lg"
                    />
                    {selectedReservation.guest?.phone && (
                      <p className="text-sm text-muted-foreground">{selectedReservation.guest.phone}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Arrivée</p>
                      <p className="font-medium">
                        {format(new Date(selectedReservation.check_in), 'dd/MM/yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Départ</p>
                      <p className="font-medium">
                        {format(new Date(selectedReservation.check_out), 'dd/MM/yyyy')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nuits</p>
                      <p className="font-medium">{selectedReservation.nights}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Occupants</p>
                      <p className="font-medium">{selectedReservation.total_pax} pax</p>
                    </div>
                  </div>

                  {/* Acompte section for non-OTA reservations (requires deposit handling) */}
                  {canSeeFinancials && (() => {
                    const channelConfig = configOptions.CHANNEL?.find(ch => ch.code === selectedReservation.source);
                    const isOta = channelConfig?.metadata?.is_ota === true;
                    return !isOta;
                  })() && (
                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800 mb-2">Acompte (Réservation hors OTA)</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Acompte reçu:</span>
                          <span className="ml-1 font-medium">
                            {selectedReservation.deposit_amount > 0 
                              ? formatCurrency(selectedReservation.deposit_amount, selectedReservation.currency || 'MAD')
                              : 'Non renseigné'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Solde:</span>
                          <span className="ml-1 font-medium">
                            {formatCurrency(
                              (selectedReservation.total_amount || 0) - (selectedReservation.deposit_amount || 0),
                              selectedReservation.currency || 'MAD'
                            )}
                          </span>
                        </div>
                        {selectedReservation.balance_reminder_date && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">Demander solde le:</span>
                            <span className="ml-1 font-medium">
                              {format(new Date(selectedReservation.balance_reminder_date), 'dd/MM/yyyy')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {canSeeFinancials && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground">Montant total</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(selectedReservation.total_amount, selectedReservation.currency)}
                      </p>
                      {(() => {
                        const channelConfig = configOptions.CHANNEL?.find(ch => ch.code === selectedReservation.source);
                        const defaultFlux = channelConfig?.metadata?.default_flux;
                        if (!channelConfig?.metadata?.is_ota) {
                          return (
                            <p className="text-xs text-muted-foreground mt-1">
                              Versement sur {defaultFlux === 'COMPTE_PERSO' ? 'compte personnel' : defaultFlux || 'caisse'}
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setSelectedReservation(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Detail Modal */}
      <Dialog open={!!selectedService} onOpenChange={(open) => !open && setSelectedService(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedService && (() => {
                const ServiceIcon = getServiceIconComponent(selectedService.type);
                const serviceColor = getServiceColor(selectedService.type);
                return (
                  <>
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${serviceColor}20` }}
                    >
                      <ServiceIcon className="w-4 h-4" style={{ color: serviceColor }} />
                    </div>
                    <span>{selectedService.title}</span>
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4">
              {/* Service Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <Badge 
                    variant="outline"
                    style={{ 
                      borderColor: getServiceColor(selectedService.type),
                      color: getServiceColor(selectedService.type)
                    }}
                  >
                    {eventTypeMap[selectedService.type]?.label || selectedService.type}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Statut</Label>
                  <Badge className={
                    selectedService.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                    selectedService.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    selectedService.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {selectedService.status === 'CONFIRMED' ? 'Confirmé' :
                     selectedService.status === 'PENDING' ? 'En attente' :
                     selectedService.status === 'CANCELLED' ? 'Annulé' :
                     selectedService.status}
                  </Badge>
                </div>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date
                  </Label>
                  <p className="font-medium">
                    {format(new Date(selectedService.event_date), 'EEEE dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
                {selectedService.event_time && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Heure
                    </Label>
                    <p className="font-medium">{selectedService.event_time}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedService.description && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <p className="text-sm bg-muted/50 p-2 rounded">{selectedService.description}</p>
                </div>
              )}

              {/* Reservation & Guest Info */}
              {selectedService.reservation && (
                <div className="border-t pt-4 space-y-3">
                  <Label className="text-xs text-muted-foreground">Réservation associée</Label>
                  <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg">
                    <div className="space-y-1">
                      <ReservationLink 
                        reservation={{ 
                          id: selectedService.reservation.id, 
                          reservation_number: selectedService.reservation.reservation_number 
                        }} 
                        variant="badge"
                      />
                      {selectedService.reservation.guest && (
                        <div className="flex items-center gap-1 text-sm">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <GuestLink 
                            guest={selectedService.reservation.guest}
                            showIcon={false}
                          />
                        </div>
                      )}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      {selectedService.reservation.check_in && (
                        <p>
                          {format(new Date(selectedService.reservation.check_in), 'dd/MM')} - {format(new Date(selectedService.reservation.check_out), 'dd/MM/yyyy')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedService(null)}
                >
                  Fermer
                </Button>
                <Button 
                  onClick={() => {
                    navigate(`/reservations?id=${selectedService.reservation_id}`);
                    setSelectedService(null);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Voir la réservation
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
